// src/components/ui/LocationSuccess.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCheckCircle } from 'react-icons/fa';

interface LocationSuccessProps {
    message: string;
    theme?: {
        primaryColor: string;
        textColor: string;
        backgroundColor: string;
    };
    className?: string;
}

const LocationSuccess: React.FC<LocationSuccessProps> = ({
    message,
    theme,
    className = "",
}) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className={`p-4 rounded-xl border ${className}`}
            style={{
                backgroundColor: `${theme?.primaryColor || "#22c55e"}08`,
                borderColor: `${theme?.primaryColor || "#22c55e"}40`,
            }}
        >
            <div className="flex items-center gap-3">
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                    className="relative"
                >
                    <FaMapMarkerAlt
                        size={20}
                        style={{ color: theme?.primaryColor || "#22c55e" }}
                    />
                    <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                        className="absolute -top-1 -right-1"
                    >
                        <FaCheckCircle
                            size={12}
                            className="text-green-500 bg-white rounded-full"
                        />
                    </motion.div>
                </motion.div>

                <motion.p
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-sm font-medium flex-1"
                    style={{ color: theme?.primaryColor || "#22c55e" }}
                >
                    {message}
                </motion.p>
            </div>

            {/* Success particles effect */}
            <motion.div
                className="absolute inset-0 pointer-events-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 0] }}
                transition={{ duration: 1, delay: 0.5 }}
            >
                {[...Array(6)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-1 h-1 rounded-full"
                        style={{
                            backgroundColor: theme?.primaryColor || "#22c55e",
                            left: `${20 + i * 10}%`,
                            top: `${30 + (i % 2) * 20}%`,
                        }}
                        animate={{
                            y: [-5, -15, -5],
                            opacity: [0, 1, 0],
                            scale: [0, 1, 0],
                        }}
                        transition={{
                            duration: 1,
                            delay: 0.5 + i * 0.1,
                            ease: "easeOut",
                        }}
                    />
                ))}
            </motion.div>
        </motion.div>
    );
};

export default LocationSuccess;